import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import FocusGroupAnalyticsSummary from './FocusGroupAnalyticsSummary';

function FocusGroupFeedback({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterClassCode, setFilterClassCode] = useState('all');
  const printRef = useRef();

  useEffect(() => {
    fetchSubmissions();
  }, [filterType, filterClassCode]);

  const fetchSubmissions = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.set('type', filterType);
      if (filterClassCode !== 'all') params.set('classCode', filterClassCode);

      const response = await axios.get(`/api/focus-group/all-submissions?${params}`, {
        withCredentials: true,
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatSubmissionType = (type) => {
    if (!type) return '-';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const renderResponseValue = (response) => {
    const val = response.value;
    if (val === undefined || val === null || val === '') {
      return <span className="text-gray-400 italic">No response</span>;
    }
    if (response.responseType === 'rating') {
      const num = Number(val);
      const max = 5;
      return (
        <span className="inline-flex items-center space-x-1">
          <span className="font-semibold text-indigo-700">{num}/{max}</span>
          <span className="text-amber-400 text-xs tracking-tight">
            {'★'.repeat(num)}{'☆'.repeat(Math.max(0, max - num))}
          </span>
        </span>
      );
    }
    if (response.responseType === 'multipleChoice') {
      return <span className="inline-block px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-sm font-medium">{String(val)}</span>;
    }
    return <span className="text-sm text-gray-700">{String(val)}</span>;
  };

  const renderResponses = (responses) => {
    if (!Array.isArray(responses) || responses.length === 0) return null;
    return (
      <div className="space-y-3">
        {responses.map((r, i) => (
          <div key={i} className="border-l-2 border-indigo-200 pl-3">
            <p className="text-xs font-medium text-gray-500 mb-0.5">{r.questionText || r.questionId || `Question ${i + 1}`}</p>
            <div>{renderResponseValue(r)}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderSubmissionData = (submission) => {
    const d = submission.data;
    if (!d) return <span className="text-gray-400">No data</span>;

    if (typeof d === 'string') {
      return <p className="text-sm text-gray-700 whitespace-pre-wrap">{d}</p>;
    }

    if (Array.isArray(d)) {
      return renderResponses(d) || (
        <div className="space-y-2">
          {d.map((item, i) => (
            <div key={i} className="border-l-2 border-indigo-200 pl-3">
              {item.text && <p className="text-xs font-medium text-gray-500">{item.text}</p>}
              {item.answer !== undefined && (
                <p className="text-sm text-gray-700">{typeof item.answer === 'object' ? JSON.stringify(item.answer) : String(item.answer)}</p>
              )}
              {item.value !== undefined && (
                <p className="text-sm text-gray-700">{String(item.value)}</p>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Object with a responses array (e.g. { responses: [...], surveyType: '...' })
    if (typeof d === 'object' && Array.isArray(d.responses)) {
      return renderResponses(d.responses);
    }

    if (typeof d === 'object') {
      return (
        <div className="space-y-1">
          {Object.entries(d).map(([key, val]) => (
            <div key={key} className="border-l-2 border-indigo-200 pl-3">
              <span className="text-xs font-medium text-gray-500">{key.replace(/_/g, ' ')}: </span>
              <span className="text-sm text-gray-700">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
            </div>
          ))}
        </div>
      );
    }

    return <p className="text-sm text-gray-700">{String(d)}</p>;
  };

  const handlePrint = () => {
    const content = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Focus Group Feedback${filterClassCode !== 'all' ? ` - ${filterClassCode}` : ''}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #1a1a1a; }
            h1 { font-size: 20px; margin-bottom: 4px; }
            .subtitle { color: #666; font-size: 14px; margin-bottom: 24px; }
            .stats { display: flex; gap: 24px; margin-bottom: 24px; padding: 12px 0; border-bottom: 1px solid #ddd; }
            .stat { text-align: center; }
            .stat-num { font-size: 20px; font-weight: 700; }
            .stat-label { font-size: 11px; color: #666; }
            .submission { border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px; margin-bottom: 12px; page-break-inside: avoid; }
            .submission-header { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 13px; color: #555; }
            .submission-type { font-weight: 600; text-transform: capitalize; }
            .submission-data { font-size: 13px; line-height: 1.6; }
            .submission-data .item { border-left: 2px solid #c7d2fe; padding-left: 8px; margin-bottom: 6px; }
            .item-label { font-size: 11px; font-weight: 600; color: #888; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Focus Group Feedback</h1>
          <div class="subtitle">${filterClassCode !== 'all' ? `Group: ${filterClassCode} | ` : ''}${filterType !== 'all' ? `Type: ${formatSubmissionType(filterType)} | ` : ''}Generated ${new Date().toLocaleDateString()}</div>
          ${data?.stats ? `
          <div class="stats">
            <div class="stat"><div class="stat-num">${data.stats.totalSubmissions}</div><div class="stat-label">Total Submissions</div></div>
            <div class="stat"><div class="stat-num">${data.stats.progress.totalParticipants}</div><div class="stat-label">Participants</div></div>
            <div class="stat"><div class="stat-num">${data.stats.progress.evaluationsCompleted}</div><div class="stat-label">Evaluations</div></div>
            <div class="stat"><div class="stat-num">${data.stats.progress.totalJournalSessions}</div><div class="stat-label">Journal Sessions</div></div>
          </div>` : ''}
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sage-400 border-t-transparent"></div>
      </div>
    );
  }

  const submissions = data?.submissions || [];
  const stats = data?.stats || {};
  const filters = data?.filters || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link to="/focus-groups" className="text-forest-800 hover:text-forest-700">
                ← Focus Groups
              </Link>
              <h1 className="text-2xl font-bold text-forest-800">
                Feedback & Submissions
              </h1>
            </div>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>Print / PDF</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-forest-800">{stats.totalSubmissions || 0}</div>
            <div className="text-sm text-gray-600">Total Submissions</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.progress?.totalParticipants || 0}</div>
            <div className="text-sm text-gray-600">Participants</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-green-600">{stats.progress?.firstImpressionsCompleted || 0}</div>
            <div className="text-sm text-gray-600">First Impressions</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-amber-600">{stats.progress?.midStudyCompleted || 0}</div>
            <div className="text-sm text-gray-600">Mid-Study</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.progress?.evaluationsCompleted || 0}</div>
            <div className="text-sm text-gray-600">Evaluations</div>
          </div>
        </div>

        {/* Analytics Summary */}
        <FocusGroupAnalyticsSummary filterClassCode={filterClassCode} />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Group:</label>
            <select
              value={filterClassCode}
              onChange={(e) => setFilterClassCode(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Groups</option>
              {(filters.classCodes || []).map(code => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Type:</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Types</option>
              {(filters.submissionTypes || []).map(t => (
                <option key={t} value={t}>{formatSubmissionType(t)}</option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500">
            Showing {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Submissions List (printable) */}
        <div ref={printRef}>
          {submissions.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
              No submissions found for the selected filters.
            </div>
          ) : (
            <div className="space-y-4">
              {submissions.map((sub) => (
                <div key={sub.id} className="submission bg-white rounded-lg shadow p-5">
                  <div className="submission-header flex justify-between items-start mb-3">
                    <div>
                      <span className="submission-type inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                        {formatSubmissionType(sub.submissionType)}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">{sub.classCode}</span>
                    </div>
                    <span className="text-sm text-gray-400">{formatDate(sub.submittedAt)}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-sm font-medium text-gray-700">{sub.email}</span>
                    {sub.journalSessionCount !== null && (
                      <span className="ml-3 text-xs text-gray-400">
                        {sub.journalSessionCount} sessions | {sub.completionPercentage || 0}% complete
                      </span>
                    )}
                  </div>
                  <div className="submission-data mt-3 bg-gray-50 rounded-md p-3">
                    {renderSubmissionData(sub)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default FocusGroupFeedback;
